import { FormField } from '@/components/form-section'
import type { Room } from '@/lib/api/rooms/rooms-api'
import type { ServiceType } from '../types'

interface ScheduleWhenFieldsProps {
    isBudgetMode: boolean
    isTeleconsultation: boolean
    serviceType: ServiceType
    scheduledDate: string
    onScheduledDateChange: (value: string) => void
    scheduledTime: string
    onScheduledTimeChange: (value: string) => void
    /** Ordem de chegada: tem data, não tem hora. */
    arrivalOrder: boolean
    onArrivalOrderChange: (value: boolean) => void
    validUntil: string
    onValidUntilChange: (value: string) => void
    rooms: Room[]
    roomId: number | null
    onRoomChange: (roomId: number | null) => void
}

/**
 * Quando e onde o atendimento acontece: data, hora, ordem de chegada, validade
 * do orçamento e sala.
 */
export default function ScheduleWhenFields({
    isBudgetMode,
    isTeleconsultation,
    serviceType,
    scheduledDate,
    onScheduledDateChange,
    scheduledTime,
    onScheduledTimeChange,
    arrivalOrder,
    onArrivalOrderChange,
    validUntil,
    onValidUntilChange,
    rooms,
    roomId,
    onRoomChange,
}: ScheduleWhenFieldsProps) {
    return (
        <div className="flex flex-col gap-5">
            {!isBudgetMode && (
                <FormField label="Data e hora do atendimento" optional>
                    <div className="flex flex-wrap gap-3">
                        <input
                            type="date"
                            aria-label="Data do atendimento"
                            className="input input-bordered min-w-40 flex-1"
                            value={scheduledDate}
                            onChange={e =>
                                onScheduledDateChange(e.target.value)
                            }
                        />
                        {/* Hora desabilitada por ordem de chegada: não há horário
                            a informar, e deixar o campo ativo convidava o
                            operador a inventar um. */}
                        <input
                            type="time"
                            aria-label="Hora do atendimento"
                            className="input input-bordered min-w-32 flex-1"
                            value={arrivalOrder ? '' : scheduledTime}
                            disabled={arrivalOrder}
                            onChange={e =>
                                onScheduledTimeChange(e.target.value)
                            }
                        />
                    </div>
                    <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-base-300 px-3 py-2">
                        <input
                            type="checkbox"
                            className="checkbox checkbox-sm"
                            checked={arrivalOrder}
                            onChange={e =>
                                onArrivalOrderChange(e.target.checked)
                            }
                        />
                        <span className="text-sm text-base-content/80">
                            Atendimento por ordem de chegada (sem hora marcada)
                        </span>
                    </label>
                </FormField>
            )}

            {(serviceType === 'EXAM' || serviceType === 'PROCEDURE') &&
                isBudgetMode && (
                    <FormField
                        label="Validade do orçamento"
                        htmlFor="agendamento-validade"
                    >
                        <input
                            id="agendamento-validade"
                            type="date"
                            className="input input-bordered w-full"
                            value={validUntil}
                            onChange={e => onValidUntilChange(e.target.value)}
                        />
                    </FormField>
                )}

            {/* Sala/Consultório — consulta, exame e procedimento. */}
            {!isTeleconsultation && rooms.length > 0 && (
                <FormField
                    label="Sala / Consultório"
                    htmlFor="agendamento-sala"
                    optional
                >
                    <select
                        id="agendamento-sala"
                        className="select select-bordered w-full"
                        value={roomId || ''}
                        onChange={e =>
                            onRoomChange(
                                e.target.value ? parseInt(e.target.value) : null
                            )
                        }
                    >
                        <option value="">Nenhuma</option>
                        {rooms.map(room => (
                            <option key={room.id} value={room.id}>
                                {room.name}
                                {room.description
                                    ? ` - ${room.description}`
                                    : ''}
                            </option>
                        ))}
                    </select>
                </FormField>
            )}
        </div>
    )
}
